import type { Country } from '@eliadmin/core/src/api/general/types/general-types';

/**
 * The core `PhoneInputField` stores E.164 („+421905123456“) and the core schemas demand 12
 * characters. The design shows a plain field („+421...“), so the typed value is normalised to the
 * same format: spaces and separators dropped, „00“ read as „+“, a national „0…“ number taken as
 * Slovak.
 */
export function normalizePhone(value: string) {
  const compact = value.replace(/[\s().-]/g, '');
  if (compact.startsWith('+')) return `+${compact.slice(1).replace(/\D/g, '')}`;
  const digits = compact.replace(/\D/g, '');
  if (digits.startsWith('00')) return `+${digits.slice(2)}`;
  if (digits.startsWith('0')) return `+421${digits.slice(1)}`;
  return digits.startsWith('421') ? `+${digits}` : digits;
}

/** „+421 905 123 456“ – a SK / CZ number grouped as in the design, other numbers as normalized. */
export function formatPhone(value: string) {
  const phone = normalizePhone(value);
  const match = /^\+(42[01])(\d{3})(\d{3})(\d{3})$/.exec(phone);
  return match ? `+${match.slice(1).join(' ')}` : phone;
}

/** „974 01“ – Slovak and Czech postcodes are written in two groups; others stay as given. */
export function formatPostcode(value: string | null | undefined, countryId?: string | null) {
  const code = (value ?? '').replace(/\s/g, '');
  return /^\d{5}$/.test(code) && (countryId === 'SK' || countryId === 'CZ')
    ? `${code.slice(0, 3)} ${code.slice(3)}`
    : (value ?? '');
}

export function isValidPhone(value: string) {
  return /^\+\d{11,15}$/.test(normalizePhone(value));
}

/**
 * The design has one „Ulica a číslo domu“ field, the customer record (and the checkout) keeps the
 * street and the house number apart: the last word that starts with a digit is the number.
 */
export function splitStreet(value: string) {
  const match = /^(.*\S)\s+(\d[\w/.-]*)$/u.exec(value.trim());
  return match ? { street: match[1], number: match[2] } : { street: value.trim(), number: '' };
}

/** „Meno Priezvisko“ of the delivery contact: the last word is the surname. */
export function splitFullName(value: string) {
  const trimmed = value.trim();
  const index = trimmed.lastIndexOf(' ');
  return index === -1
    ? { name: trimmed, surname: '' }
    : { name: trimmed.slice(0, index).trim(), surname: trimmed.slice(index + 1) };
}

/** The API flags the store's default country (`is_default`, not in the core type); Slovakia, then the first one, otherwise. */
export function defaultCountryId(countries: (Country & { is_default?: boolean })[]) {
  const preferred =
    countries.find(country => country.is_default) ??
    countries.find(country => country.id === 'SK') ??
    countries[0];
  return preferred?.id ?? '';
}
